import { useState, useMemo } from 'react';
import { Card, Typography, Form, Input, InputNumber, DatePicker, Select, Checkbox, Tag, Button, Space, Spin, message } from 'antd';
import { SafetyCertificateOutlined, ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/use_api';
import { usePersonFieldsState } from '../../hooks/use_person_fields';
import GeoLocationInput from '../Entities/GeoLocationInput';
import PieceBackdrop from '../Skargard/PieceBackdrop';
import Fold from '../Skargard/Fold';
import Footer from '../../Routes/Footer';

const { Title, Text, Paragraph } = Typography;

// Built-in Person fields (see Account/PersonFields.jsx) — always present,
// alongside whatever custom fields this node has *enabled* (the hook below
// reads /api/person-fields/enabled, so a disabled field never gets a page).
// Each becomes its own page in the wizard below.
//
// URL and email are deliberately absent: both columns exist on Entity, but
// neither is a Person field — CreateEntityForm hides them for people and
// Entity.jsx leaves them off a person's details. Onboarding was the last
// screen still asking, so it asked for something no node offers.
const BUILTIN_FIELD_STEPS = [
    {
        formName: 'name',
        label: 'Name',
        rules: [{ required: true, message: 'Please enter your name' }],
        input: <Input placeholder="Your name" autoFocus />,
    },
    {
        formName: 'description',
        label: 'Description',
        rules: [],
        // The entity's default EntityDescription, which the API surfaces as a
        // plain `description` field on the entity (a read-only column_property
        // on the way out, upserted on the way in).
        input: <Input.TextArea autoSize={{ minRows: 3, maxRows: 10 }} placeholder="A few words about you" />,
    },
];

function customFieldInput(f) {
    switch (f.field_type) {
        case 'number': return <InputNumber style={{ width: '100%' }} placeholder={f.label} />;
        case 'date': return <DatePicker style={{ width: '100%' }} placeholder={f.label} />;
        case 'geo': return <GeoLocationInput />;
        case 'geo_multi': return <GeoLocationInput multiple />;
        case 'select': return <Select allowClear placeholder={f.label} options={(f.options || []).map(o => ({ value: o, label: o }))} />;
        default: return <Input placeholder={f.label} />;
    }
}

// One consent per card, same shape as ConsentScreen's list, but rendered as
// a single page in the wizard rather than gating a separate "Continue".
function ConsentsStep({ consents, checked, onCheck }) {
    if (consents.length === 0) {
        return <Text type="secondary">Nothing to review right now.</Text>;
    }
    return consents.map(c => (
        <div key={c.id} style={{
            border: '1px solid var(--border-color)',
            borderRadius: 8,
            padding: 'var(--sp-3) var(--sp-4)',
            marginBottom: 'var(--sp-3)',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <Text strong>{c.title}</Text>
                {c.required ? <Tag color="red">Required</Tag> : <Tag>Optional</Tag>}
            </div>
            <Paragraph
                type="secondary"
                style={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto', marginBottom: 8 }}
            >
                {c.body}
            </Paragraph>
            <Checkbox
                checked={!!checked[c.id]}
                onChange={e => onCheck(c.id, e.target.checked)}
            >
                {c.required ? 'I agree' : 'Yes, I agree (optional)'}
            </Checkbox>
        </div>
    ));
}

/**
 * Full-screen onboarding for a user whose local registration is still
 * pending. Lets them get set up while they wait for a server admin to
 * review: answer this node's consents (all together, on the first page),
 * then their Person entity's fields one per page, with Back/Next between
 * every page. Rendered by ProtectedRoute instead of RegistrationStatusScreen
 * for 'pending' users.
 *
 * Extension point for the future: node admins will be able to edit a
 * custom welcome message shown here (not yet built).
 */
function OnboardingScreen({ consents, onSubmitConsents }) {
    const { logout, userPersonEntity, user } = useAuth();
    const api = useApi();
    // `loaded` matters here: this wizard's page count *is* the field count, so
    // rendering a field page before the node's enabled set has arrived would
    // label the Name page 'Finish' and mount the Form with no initial values
    // for the custom fields (antd only applies initialValues once, at mount).
    const { fields: personFields, loaded: fieldsLoaded } = usePersonFieldsState();
    const [form] = Form.useForm();
    const [step, setStep] = useState(0);
    const [checked, setChecked] = useState({});
    const [consentsSaving, setConsentsSaving] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);

    const fieldSteps = useMemo(() => [
        ...BUILTIN_FIELD_STEPS,
        ...personFields.map(f => ({
            formName: ['person_fields', f.key],
            label: f.label,
            rules: [],
            input: customFieldInput(f),
        })),
    ], [personFields]);

    // 'consents' is only a page when there's something to answer; 'done' is
    // always the last page, once every field has had its own turn.
    const pages = useMemo(() => [
        ...(consents.length > 0 ? [{ type: 'consents' }] : []),
        ...fieldSteps.map(f => ({ type: 'field', ...f })),
        { type: 'done' },
    ], [consents.length, fieldSteps]);

    const page = pages[step];
    const isFirst = step === 0;
    const isLastField = pages[step + 1]?.type === 'done';

    // Whatever is already on the record shows up prefilled, so a returning user
    // (or one whose details arrived by CSV import) edits rather than retypes.
    // Attributes are plain JSON, so a 'date' comes back as the ISO string
    // saveProfile wrote and DatePicker needs a dayjs object to render it — the
    // same conversion InlineEditField does when it opens an editor. Every other
    // type round-trips as-is; a 'geo' value is the { label, lat, lng } object
    // GeoLocationInput reads straight back.
    const personFieldValue = f => {
        const v = userPersonEntity?.attributes?.[f.key];
        if (v === undefined || v === null) return undefined;
        return f.field_type === 'date' ? dayjs(v) : v;
    };

    const initialValues = userPersonEntity ? {
        name: userPersonEntity.name,
        description: userPersonEntity.description,
        person_fields: Object.fromEntries(
            personFields.map(f => [f.key, personFieldValue(f)]),
        ),
    } : undefined;

    const saveProfile = async () => {
        const values = form.getFieldsValue(true);
        const attributes = {};
        for (const [k, v] of Object.entries(values.person_fields || {})) {
            if (v === undefined || v === null || v === '') continue;
            attributes[k] = v?.toISOString ? v.toISOString() : v;
        }
        setProfileSaving(true);
        try {
            // No `email` or `url` key: neither is a Person field, and sending
            // `values.url || null` for a field the form never rendered would
            // wipe a value an import put there — for email that is the one
            // thing the claim link needs to be sendable.
            await api.put(`/api/entities/${userPersonEntity.id}`, {
                name: values.name,
                // Blank means "didn't fill it in", not "clear it": the backend
                // treats a null description as unchanged (upsert_default_description
                // returns early), so skipping it leaves an imported one alone
                // rather than replacing it with an empty default description.
                description: values.description?.trim() || null,
                attributes,
            }, { params: { user_id: user?.id } });
            return true;
        } catch (err) {
            message.error(err?.message || 'Could not save your profile');
            return false;
        } finally {
            setProfileSaving(false);
        }
    };

    const submitConsentsStep = async () => {
        const missingRequired = consents.some(c => c.required && !checked[c.id]);
        if (missingRequired) {
            message.error('Please accept every required item before continuing.');
            return false;
        }
        setConsentsSaving(true);
        try {
            await onSubmitConsents(consents.map(c => ({ consent_id: c.id, version: c.version, accepted: !!checked[c.id] })));
            return true;
        } catch (err) {
            message.error(err.status === 409
                ? 'Something was just updated — please review it again.'
                : (err.message || 'Could not save your choices'));
            return false;
        } finally {
            setConsentsSaving(false);
        }
    };

    const handleNext = async () => {
        if (page.type === 'consents') {
            if (await submitConsentsStep()) setStep(s => s + 1);
            return;
        }
        // Only the current page's field needs to be valid to move on — the
        // others live on pages the user hasn't necessarily reached yet.
        try {
            await form.validateFields([page.formName]);
        } catch {
            return;
        }
        if (await saveProfile()) setStep(s => s + 1);
    };

    const handleBack = () => setStep(s => s - 1);

    const busy = consentsSaving || profileSaving;

    return (
        // Fold, the same piece as the hangout waiting room (Hangouts/EntityCall.jsx).
        // Both screens are a wait with a single card of chrome over them, so they
        // read as one moment rather than two different products. It states no
        // ground of its own (groundSaturation 0), so it paints on --art-paper and
        // follows the user's theme.
        <PieceBackdrop piece={Fold}>
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
                <div style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    padding: 'var(--sp-8) var(--sp-4) 60px',
                }}>
                    <div style={{ maxWidth: 480, width: '100%' }}>
                        <Card style={{ textAlign: 'center', marginBottom: 'var(--sp-4)' }}>
                            <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>Let's get you set up</Title>
                            {(page.type === 'consents' || page.type === 'done') && (
                                <Text type="secondary">
                                    Your account is waiting for a server admin to approve it. While you wait,
                                    you can review this node's consents and fill in your profile.
                                </Text>
                            )}
                            {page.type === 'field' && (
                                <Text type="secondary">Step {step + 1} of {pages.length}</Text>
                            )}
                        </Card>

                        {page.type !== 'done' && (
                            <Card style={{ marginBottom: 'var(--sp-4)' }}>
                                {page.type === 'consents' ? (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                            <SafetyCertificateOutlined style={{ color: 'var(--ant-color-primary)' }} />
                                            <Title level={5} style={{ margin: 0 }}>Consents</Title>
                                        </div>
                                        <ConsentsStep
                                            consents={consents}
                                            checked={checked}
                                            onCheck={(id, value) => setChecked(prev => ({ ...prev, [id]: value }))}
                                        />
                                    </>
                                ) : userPersonEntity && fieldsLoaded ? (
                                    <Form form={form} layout="vertical" initialValues={initialValues}>
                                        <Form.Item label={page.label} name={page.formName} rules={page.rules}>
                                            {page.input}
                                        </Form.Item>
                                    </Form>
                                ) : (
                                    <Spin />
                                )}
                            </Card>
                        )}

                        {page.type === 'done' && (
                            <Card style={{ marginBottom: 'var(--sp-4)', textAlign: 'center' }}>
                                <Text>Thanks — your profile and consents are saved. We'll let you in as soon as an admin approves your account.</Text>
                            </Card>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 'var(--sp-4)' }}>
                            <Button icon={<ArrowLeftOutlined />} disabled={isFirst || busy} onClick={handleBack}>
                                Back
                            </Button>
                            {page.type !== 'done' && (
                                <Button
                                    type="primary"
                                    loading={busy}
                                    disabled={page.type === 'field' && !(userPersonEntity && fieldsLoaded)}
                                    onClick={handleNext}
                                    icon={page.type === 'field' && isLastField ? undefined : <ArrowRightOutlined />}
                                    iconPosition="end"
                                >
                                    {page.type === 'field' && isLastField ? 'Finish' : 'Next'}
                                </Button>
                            )}
                        </div>

                        <div style={{ textAlign: 'center', marginBottom: 'var(--sp-4)' }}>
                            <Space>
                                <a onClick={() => window.location.reload()} style={{ cursor: 'pointer' }}>Refresh</a>
                                <Text type="secondary">·</Text>
                                <a onClick={logout} style={{ cursor: 'pointer' }}>Log out</a>
                            </Space>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </PieceBackdrop>
    );
}

export default OnboardingScreen;
